import { useAuth } from 'app/providers/auth/AuthProvider'
import { SESSION_STORAGE_KEYS } from 'common/constants/localeStorageConstants'
import { UtilService } from 'common/services/utilService'
import type { IPatientRoom } from 'features/Home/interfaces/IInfoPatient'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  getScheduleRoomsPreference,
  isSchedulePreferenceUserId,
  setScheduleRoomsPreference,
} from './schedulePreferencesStorage'
import {
  applyRoomsPreferenceChange,
  hiddenRoomIdsFromSessionSelection,
  resolveScheduleRoomOrder,
  toScheduleRoomOptions,
  visibleScheduleRoomIds,
  type ScheduleRoomsPreference,
} from './scheduleRoomsOrder'

const readSessionRoomSelection = (): string[] | null => {
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEYS.SELECTED_SCHEDULE_ROOMS)
    if (!raw) return null
    const parsed = UtilService.safeParseJSON<unknown>(raw, null)
    if (!Array.isArray(parsed)) return null
    return parsed.map(String)
  } catch {
    return null
  }
}

export const useScheduleRoomsPreference = (rooms: IPatientRoom[]) => {
  const { userId } = useAuth()
  const [preference, setPreference] = useState<ScheduleRoomsPreference>(() =>
    getScheduleRoomsPreference(userId),
  )
  const preferenceRef = useRef(preference)
  preferenceRef.current = preference
  const migratedRef = useRef(false)

  const roomOptions = useMemo(() => toScheduleRoomOptions(rooms), [rooms])

  useEffect(() => {
    const stored = getScheduleRoomsPreference(userId)
    preferenceRef.current = stored
    setPreference(stored)
    migratedRef.current = false
  }, [userId])

  useEffect(() => {
    if (!isSchedulePreferenceUserId(userId) || !roomOptions.length || migratedRef.current) return
    migratedRef.current = true

    const selectedIds = readSessionRoomSelection()
    const current = preferenceRef.current
    const hasStoredRooms = current.order.length > 0 || current.hidden.length > 0
    if (selectedIds && !hasStoredRooms) {
      const hidden = hiddenRoomIdsFromSessionSelection(roomOptions, selectedIds)
      if (hidden.length) {
        const next = setScheduleRoomsPreference(userId, { order: current.order, hidden })
        preferenceRef.current = next
        setPreference(next)
      }
    }

    try {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEYS.SELECTED_SCHEDULE_ROOMS)
    } catch {
      // Session storage can be unavailable; the in-memory preference still applies.
    }
  }, [userId, roomOptions])

  const roomOrder = useMemo(
    () =>
      resolveScheduleRoomOrder(
        roomOptions.map((room) => room.id),
        preference.order,
      ),
    [roomOptions, preference.order],
  )

  const visibleRoomIds = useMemo(
    () => visibleScheduleRoomIds(roomOptions, roomOrder, preference.hidden),
    [roomOptions, roomOrder, preference.hidden],
  )

  const onRoomsChange = useCallback(
    (change: ScheduleRoomsPreference) => {
      const next = setScheduleRoomsPreference(
        userId,
        applyRoomsPreferenceChange(
          preferenceRef.current,
          change,
          roomOptions.map((room) => room.id),
          roomOptions.filter((room) => room.locked).map((room) => room.id),
        ),
      )
      preferenceRef.current = next
      setPreference(next)
    },
    [userId, roomOptions],
  )

  return {
    roomOptions,
    roomOrder,
    hiddenRoomIds: preference.hidden,
    visibleRoomIds,
    onRoomsChange,
  }
}
